import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { eigenerSchrittLaeuft } from "./verlauf";

/* ══════════════════════════════════════════════════════════════════
   Die Schließanfrage — Zurückgeste, Zurücktaste und `Esc` für Blätter.

   **Auf Android ist die Zurückgeste eine Schließanfrage** (WHATWG, „Close
   requests"). Gibt es keinen, der sie annimmt, deutet der Browser sie als
   Schritt zurück im Verlauf — und wer ein Blatt offen hatte, verlor die
   Maske darunter gleich mit. Bis zum 17.09.2026 hörten Deckblatt und Blatt
   von unten nur auf `Esc` (40-mobiles-grundgeruest.md, Schritt 7).

   **Kein `pushState` für Blätter.** Ein Blatt ist kein Ort; ein Eintrag im
   Verlauf dafür „can easily corrupt a web application's router" (WICG,
   Close-Watcher-Erklärung). Stattdessen in dieser Reihenfolge:

   1. **`CloseWatcher`** (Chrome 126+): nimmt die Zurückgeste, die
      Zurücktaste und `Esc` an. Mehrere stapeln sich von selbst — die
      Geste schließt das zuletzt geöffnete Blatt.
   2. **Die Navigation-API**, wo es sie gibt: Ein Schritt zurück im Verlauf
      wird abgefangen, solange ein Blatt offen ist, und schließt das
      oberste. Das ist der Ersatz, wo die Geste kein `CloseWatcher` erreicht
      (Safari, Firefox) — und fängt auch den Zurück-Knopf, der kein
      Schließen kennt. Das gilt nur auf Telefon und Tablet — am Schreibtisch
      bleibt der Zurück-Knopf des Browsers, was er war. Der Browser lässt
      das Abfangen nur einmal je Bedienung zu; aussperren lässt sich damit
      niemand.
   3. **Überall `Esc`.**

   Wer ein Blatt baut, ruft `useSchliessanfrage(offen, anfrage)`; `anfrage`
   ist dasselbe wie ein Tipper auf „Schließen" — mit der Nachfrage, wenn
   etwas verloren ginge.
   ══════════════════════════════════════════════════════════════════ */

type Waechter = { onclose: (() => void) | null; destroy: () => void };
type WaechterBau = new () => Waechter;

function waechterBau(): WaechterBau | null {
  if (typeof window === "undefined") return null;
  return (window as Window & { CloseWatcher?: WaechterBau }).CloseWatcher ?? null;
}

type Eintrag = { kennung: number; anfrage: () => void };

/** Die offenen Blätter, das zuletzt geöffnete zuletzt. */
const stapel: Eintrag[] = [];

/**
 * Ist gerade ein Blatt offen — Deckblatt, Blatt von unten oder Vollbild?
 *
 * Für die neue Fassung (19.09.2026, `schale/ruhe.ts`): Über einem offenen
 * Blatt wird nicht von selbst neu geladen. Ob darin etwas Ungesichertes
 * steht, weiß nur das Blatt — offen ist hier Grund genug.
 */
export function blattOffen(): boolean {
  return stapel.length > 0;
}

/* ══════════════ Der Knopf in der Mitte schließt (Signal, Paket 5) ══════════════

   Nach app-design-muster (`fassungen/telefon/mittiger-knopf.md`, „Kreuz — über
   jedem Blatt"): Liegt ein Blatt offen, steht der Kreis der Leiste **über** ihm
   und wird zum Kreuz; das zweite Kreuz im Blattkopf fällt am Telefon weg
   (`fallen.md` Nr. 12). Dafür muss die Leiste wissen, ob etwas offen ist, und
   das oberste schließen können — mit derselben Anfrage wie Griff, `Esc` und
   Zurückgeste, also auch mit der Rückfrage „Verwerfen?". */
const stapelHorcher = new Set<() => void>();
function stapelMelden(): void {
  for (const h of stapelHorcher) h();
}
function stapelAbonnieren(h: () => void): () => void {
  stapelHorcher.add(h);
  return () => { stapelHorcher.delete(h); };
}
/** Ob gerade ein Blatt (oder Deckblatt) offen ist — als Hook, damit die Leiste umschlägt. */
export function useBlattOffen(): boolean {
  return useSyncExternalStore(stapelAbonnieren, () => stapel.length > 0, () => false);
}
/** Das oberste offene Blatt schließen — wie ein Tipp auf sein Kreuz. */
export function oberstesSchliessen(): void {
  stapel[stapel.length - 1]?.anfrage();
}
let naechste = 1;
let horchtNavigation = false;

type Navigationsereignis = Event & {
  navigationType: string;
  cancelable: boolean;
  destination: { index: number };
};
type Navigation = EventTarget & { currentEntry: { index: number } | null };

/** Der Ersatz: ein Schritt zurück im Verlauf schließt das oberste Blatt. */
function navigationHorchen(): void {
  if (horchtNavigation || typeof window === "undefined") return;
  const nav = (window as Window & { navigation?: Navigation }).navigation;
  if (!nav) return;
  horchtNavigation = true;
  nav.addEventListener("navigate", (roh) => {
    const e = roh as Navigationsereignis;
    const oben = stapel[stapel.length - 1];
    if (!oben || e.navigationType !== "traverse" || !e.cancelable) return;
    if (document.documentElement.dataset["geraet"] === "schreibtisch") return;
    if (eigenerSchrittLaeuft()) return;
    const hier = nav.currentEntry?.index ?? 0;
    if (e.destination.index >= hier) return;
    e.preventDefault();
    oben.anfrage();
  });
}

/**
 * Hängt ein offenes Blatt an die Schließanfrage.
 *
 * Gibt zurück, ob das Blatt gerade das oberste ist — für die eigene
 * Tastenfalle (Tab bleibt im obersten Blatt).
 */
export function useSchliessanfrage(offen: boolean, anfrage: () => void): () => boolean {
  const aktuell = useRef(anfrage);
  aktuell.current = anfrage;
  const kennung = useRef(0);
  /* Nach einer Anfrage, auf die das Blatt offen bleibt (die Nachfrage
     „Verwerfen?"), ist der Wächter verbraucht. Die Runde baut einen neuen. */
  const [runde, setzeRunde] = useState(0);

  useEffect(() => {
    if (!offen) return undefined;
    const eintrag: Eintrag = { kennung: naechste++, anfrage: () => aktuell.current() };
    kennung.current = eintrag.kennung;
    stapel.push(eintrag);
    stapelMelden();
    navigationHorchen();

    const Bau = waechterBau();
    let waechter: Waechter | null = null;
    if (Bau) {
      try {
        waechter = new Bau();
        waechter.onclose = () => {
          waechter = null;
          aktuell.current();
          setzeRunde((r) => r + 1);
        };
      } catch {
        /* Ohne Bedienung verweigert der Browser weitere Wächter — dann bleibt `Esc`. */
        waechter = null;
      }
    }

    /* `Esc`, in der Fangphase: Das oberste Blatt ist vor allem anderen dran,
       was auf `Esc` hört — dem Kalender darunter, der Seitenleiste, dem Menü
       im Kopf. Gibt es einen Wächter, schließt der; die Taste wird nur
       angehalten und nicht verbraucht, sonst käme sie bei ihm nicht an. */
    const taste = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || stapel[stapel.length - 1]?.kennung !== eintrag.kennung) return;
      e.stopPropagation();
      if (waechter) return;
      e.preventDefault();
      aktuell.current();
    };
    document.addEventListener("keydown", taste, true);

    return () => {
      document.removeEventListener("keydown", taste, true);
      waechter?.destroy();
      const wo = stapel.findIndex((x) => x.kennung === eintrag.kennung);
      if (wo >= 0) { stapel.splice(wo, 1); stapelMelden(); }
    };
  }, [offen, runde]);

  return useCallback(() => stapel[stapel.length - 1]?.kennung === kennung.current, []);
}
